import React, { useState, useEffect } from 'react';
import { useMyContext } from '../../../context/MyContext';
import NavBar from '../../../component/b2c/navBar/navBar';
import Sidebar from '../../../component/b2c/sidebar/sidebar';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import axios from 'axios';
import {
    FaUserPlus, FaUserCog, FaEnvelope, FaLock, FaPhone,
    FaBuilding, FaBriefcase, FaShieldAlt, FaCheckCircle,
    FaRocket, FaMagic, FaUserTie
} from "react-icons/fa";
import { toast } from 'react-toastify';
import '../../../css/custom-scrollbar.css';
import { useNavigate } from 'react-router-dom';
import Select from "react-dropdown-select";
import { Base_Url, addEmployee } from '../../../config/config';
import { FiEye, FiEyeOff } from 'react-icons/fi';

const schema = z.object({
    name: z.string().min(1, "Name is required"),
    email: z.string().email("Invalid email address"),
    password: z.string().min(8, "Password must be at least 8 characters"),
    department: z.string().min(1, "Department is required"),
    phone: z.string().length(10, "Phone number must be 10 digits"),
    role: z.string().min(1, "Role is required"),
    company_name: z.string().min(1, "Company name is required")
});

const AddUser = () => {
    const token = localStorage.getItem('token');
    const naviagteToAllEmployee = useNavigate();
    const cus_id = localStorage.getItem('cus_id');
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { isToggleSidebar, isDarkMode, setIsToggleSidebar } = useMyContext();
    const [roles, setRoles] = useState([
        { id: "PXROLE67e68d8fd9990", name: "Admin" },
        { id: "PXROLE67e68d8fd9991", name: "Manager" },
        { id: "PXROLE67e68d8fd9992", name: "Employee" }
    ]);

    const {
        register,
        handleSubmit,
        formState: { errors, touchedFields },
        reset,
        setValue,
        watch,
    } = useForm({
        resolver: zodResolver(schema),
        mode: 'onChange',
        defaultValues: {
            name: "",
            email: "",
            password: "",
            department: "",
            phone: "",
            role: "",
            company_name: ""
        }
    });

    const watchedValues = watch();
    const filledCount = Object.values(watchedValues).filter(v => v && v.toString().trim() !== '').length;
    const totalFields = 7;
    const progress = Math.round((filledCount / totalFields) * 100);

    const onErrors = () => {
        toast.error("Please fix the errors in the form.");
    };

    const handleInputChange = (event) => {
        const { value, id } = event.target;

        if (id === 'phone') {
            const filteredValue = value.replace(/[^0-9]/g, '');
            event.target.value = filteredValue;
        }

        if (['name', 'department', 'company_name'].includes(id)) {
            const filteredValue = value.replace(/[^a-zA-Z\s]/g, '');
            event.target.value = filteredValue;
        }
    };

    const handleSelectChange = (selectedValues) => {
        if (selectedValues && selectedValues.length > 0) {
            setValue('role', selectedValues[0].id, { shouldValidate: true });
        }
    };

    const onSubmit = async (data) => {
        const apiurl = Base_Url + addEmployee;
        setIsSubmitting(true);
        try {
            const response = await axios.post(
                apiurl,
                data,
                {
                    headers: {
                        token,
                        'Content-Type': 'application/json',
                        cus_id: cus_id
                    }
                }
            );
            if (response.data.status === "SUCCESS") {
                toast.success(response.data.message);
                naviagteToAllEmployee("/allEmployee");
                reset();
                setIsSubmitting(false);
            }
            else {
                toast.error(response.data.message?.email || response.data.message?.password);
                setIsSubmitting(false);
            }
        } catch (error) {
            toast.error(error || error?.email?.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    // ✅ Premium input styling
    const inputClass = (hasError) =>
        `peer w-full pl-10 pr-3 py-2.5 text-[12px] rounded-xl border transition-all duration-300 outline-none ${
            isDarkMode
                ? 'bg-white/[0.03] text-white placeholder-transparent border-white/10 hover:border-white/20'
                : 'bg-white text-gray-800 placeholder-transparent border-gray-200'
        } ${
            hasError
                ? 'border-rose-400 dark:border-rose-500/60 focus:ring-2 focus:ring-rose-500/20'
                : isDarkMode
                    ? 'focus:ring-2 focus:ring-indigo-500/25 focus:border-indigo-500/60 focus:bg-white/[0.06] focus:shadow-[0_0_20px_rgba(99,102,241,0.15)]'
                    : 'focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 focus:shadow-[0_0_15px_rgba(99,102,241,0.1)]'
        }`;

    const floatingLabel = (hasError) =>
        `absolute left-10 top-1/2 -translate-y-1/2 text-[11px] font-medium pointer-events-none transition-all duration-200
        peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-[11px] peer-placeholder-shown:-translate-y-1/2
        peer-focus:top-0 peer-focus:text-[9px] peer-focus:-translate-y-1/2 peer-focus:px-1.5 peer-focus:rounded-md
        peer-[:not(:placeholder-shown)]:top-0 peer-[:not(:placeholder-shown)]:text-[9px] peer-[:not(:placeholder-shown)]:-translate-y-1/2 peer-[:not(:placeholder-shown)]:px-1.5 peer-[:not(:placeholder-shown)]:rounded-md
        ${isDarkMode
            ? 'bg-transparent peer-focus:bg-[#0F172A] peer-[:not(:placeholder-shown)]:bg-[#0F172A] text-gray-500 peer-focus:text-indigo-400'
            : 'bg-transparent peer-focus:bg-white peer-[:not(:placeholder-shown)]:bg-white text-gray-400 peer-focus:text-indigo-600'
        } ${hasError ? 'text-rose-400' : ''}`;

    const iconWrapClass = `absolute left-2.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-lg flex items-center justify-center transition-all duration-300 z-10 ${
        isDarkMode
            ? 'bg-gradient-to-br from-white/[0.06] to-white/[0.02] border border-white/[0.08]'
            : 'bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200'
    }`;

    const errorClass = `text-[9px] mt-1 font-semibold flex items-center gap-1 ${isDarkMode ? 'text-rose-400' : 'text-red-500'}`;

    const glass =
        "backdrop-blur-2xl backdrop-saturate-150 bg-white/50 dark:bg-white/[0.06] " +
        "border border-white/60 dark:border-white/10 " +
        "shadow-[0_8px_32px_rgba(31,41,55,0.08)]";

    return (
        <div className={`h-screen flex flex-col overflow-hidden transition-colors duration-300 ${
            isDarkMode ? 'dark bg-[#0B1120]' : 'bg-gray-50'
        }`}>
            {/* Premium ambient glows */}
            {isDarkMode && (
                <>
                    <div className="pointer-events-none absolute -top-40 -left-32 h-[500px] w-[500px] rounded-full bg-indigo-600/15 blur-[120px]" />
                    <div className="pointer-events-none absolute bottom-0 -right-32 h-[400px] w-[400px] rounded-full bg-purple-600/10 blur-[100px]" />
                    <div className="pointer-events-none absolute top-1/2 left-1/2 h-[300px] w-[300px] rounded-full bg-pink-600/5 blur-[100px]" />
                </>
            )}

            <div className="z-10 shrink-0">
                <NavBar />
            </div>
            <div className="flex flex-1 overflow-hidden relative z-10">
                {isToggleSidebar && (
                    <div
                        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm md:hidden"
                        onClick={() => setIsToggleSidebar(false)}
                    />
                )}

                <div
                    className={`
                     fixed inset-y-0 left-0 z-50 w-64
                     ${isToggleSidebar ? "translate-x-0" : "-translate-x-full"}
                     md:static md:inset-auto md:z-auto md:translate-x-0
                     ${isToggleSidebar ? "md:w-64" : "md:w-[72px]"}
                     m-3
                     rounded-3xl
                     overflow-hidden
                     transition-all
                     duration-300
                     ease-in-out
                   `}
                >
                    <Sidebar />
                </div>

                {/* ✅ Premium split layout */}
                <div className={`flex-1 w-full overflow-y-auto custom-scrollbar transition-colors duration-300 ${
                    isDarkMode ? 'bg-[#0B1120]' : 'bg-gray-100'
                }`}>
                    <div className="min-h-full flex items-start lg:items-center justify-center p-3 md:p-6">
                        <div className="w-full max-w-6xl">

                            {/* Animated gradient border wrapper */}
                            <div className={`relative rounded-3xl p-[1.5px] ${
                                isDarkMode
                                    ? 'bg-gradient-to-br from-indigo-500/60 via-purple-500/40 to-pink-500/60'
                                    : 'bg-gradient-to-br from-indigo-300 via-purple-300 to-pink-300'
                            }`}>
                                {isDarkMode && (
                                    <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 opacity-30 blur-2xl animate-pulse pointer-events-none" />
                                )}

                                <div className={`relative rounded-3xl overflow-visible ${
                                    isDarkMode
                                        ? 'bg-gradient-to-br from-[#0F172A] via-[#111827] to-[#0F172A]'
                                        : 'bg-white'
                                }`}>
                                    <div className="grid grid-cols-1 lg:grid-cols-5">

                                        {/* LEFT PANEL - Branding / Info */}
                                        <div className={`lg:col-span-2 relative p-5 md:p-7 overflow-hidden rounded-t-3xl lg:rounded-tr-none lg:rounded-l-3xl ${
                                            isDarkMode
                                                ? 'bg-gradient-to-br from-indigo-600/20 via-purple-600/10 to-pink-600/15 border-b lg:border-b-0 lg:border-r border-white/[0.06]'
                                                : 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 border-b lg:border-b-0 lg:border-r border-gray-100'
                                        }`}>
                                            {/* Decorative orbs */}
                                            <div className={`absolute -top-20 -right-20 h-48 w-48 rounded-full blur-3xl pointer-events-none ${
                                                isDarkMode ? 'bg-indigo-500/30' : 'bg-indigo-300/40'
                                            }`} />
                                            <div className={`absolute -bottom-20 -left-20 h-48 w-48 rounded-full blur-3xl pointer-events-none ${
                                                isDarkMode ? 'bg-purple-500/30' : 'bg-purple-300/40'
                                            }`} />

                                            <div className="relative z-10">
                                                {/* Icon */}
                                                <div className={`inline-flex p-3 rounded-2xl mb-4 ${
                                                    isDarkMode
                                                        ? 'bg-gradient-to-br from-indigo-500 to-purple-600 shadow-[0_0_30px_rgba(139,92,246,0.5)]'
                                                        : 'bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg'
                                                }`}>
                                                    <FaUserPlus size={20} className="text-white" />
                                                </div>

                                                <h1 className={`text-xl md:text-2xl font-bold tracking-tight mb-2 ${
                                                    isDarkMode ? 'text-white' : 'text-gray-900'
                                                }`}>
                                                    Add New User
                                                </h1>
                                                <p className={`text-[11px] leading-relaxed mb-5 ${
                                                    isDarkMode ? 'text-gray-400' : 'text-gray-600'
                                                }`}>
                                                    Create a new employee account with role-based access. Fill in the details and get started.
                                                </p>

                                                {/* Feature list */}
                                                <div className="space-y-2.5">
                                                    {[
                                                        { icon: <FaShieldAlt size={10} />, text: 'Secure role-based access', color: 'text-emerald-400' },
                                                        { icon: <FaRocket size={10} />, text: 'Instant account activation', color: 'text-amber-400' },
                                                        { icon: <FaMagic size={10} />, text: 'Smart field validation', color: 'text-pink-400' },
                                                    ].map((f, i) => (
                                                        <div key={i} className="flex items-center gap-2.5">
                                                            <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${
                                                                isDarkMode
                                                                    ? 'bg-white/[0.05] border border-white/[0.08]'
                                                                    : 'bg-white border border-gray-200'
                                                            }`}>
                                                                <span className={isDarkMode ? f.color : 'text-indigo-600'}>{f.icon}</span>
                                                            </div>
                                                            <span className={`text-[10px] font-medium ${
                                                                isDarkMode ? 'text-gray-400' : 'text-gray-600'
                                                            }`}>{f.text}</span>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Progress pill */}
                                                <div className={`mt-6 p-3 rounded-xl border ${
                                                    isDarkMode
                                                        ? 'bg-white/[0.03] border-white/[0.08]'
                                                        : 'bg-white border-gray-200'
                                                }`}>
                                                    <div className="flex items-center justify-between mb-2">
                                                        <span className={`text-[9px] font-bold uppercase tracking-wider ${
                                                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                                                        }`}>Form Progress</span>
                                                        <span className={`text-[10px] font-bold ${
                                                            isDarkMode ? 'text-indigo-300' : 'text-indigo-600'
                                                        }`}>{progress}%</span>
                                                    </div>
                                                    <div className={`h-1.5 rounded-full overflow-hidden ${
                                                        isDarkMode ? 'bg-white/[0.06]' : 'bg-gray-100'
                                                    }`}>
                                                        <div
                                                            className="h-full rounded-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 transition-all duration-500"
                                                            style={{ width: `${progress}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* RIGHT PANEL - Form */}
                                        <div className="lg:col-span-3 p-4 md:p-6 relative">
                                            {/* Form Header */}
                                            <div className={`flex items-center gap-2 mb-4 pb-3 border-b ${
                                                isDarkMode ? 'border-white/[0.06]' : 'border-gray-100'
                                            }`}>
                                                <div className={`p-1.5 rounded-lg ${
                                                    isDarkMode
                                                        ? 'bg-indigo-500/10 border border-indigo-500/30'
                                                        : 'bg-indigo-50 border border-indigo-200'
                                                }`}>
                                                    <FaUserCog size={12} className={isDarkMode ? 'text-indigo-400' : 'text-indigo-600'} />
                                                </div>
                                                <span className={`text-[10px] font-bold uppercase tracking-[0.15em] ${
                                                    isDarkMode ? 'text-indigo-300' : 'text-indigo-600'
                                                }`}>Employee Details</span>
                                                <span className={`ml-auto text-[8px] font-medium ${
                                                    isDarkMode ? 'text-gray-500' : 'text-gray-400'
                                                }`}>
                                                    <span className={isDarkMode ? 'text-rose-400' : 'text-red-500'}>*</span> Required
                                                </span>
                                            </div>

                                            <form onSubmit={handleSubmit(onSubmit, onErrors)}>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">

                                                    {/* Role - Full width - HIGH Z-INDEX */}
                                                    <div className="md:col-span-2 relative" style={{ zIndex: 100 }}>
                                                        <label className={`block text-[9px] font-bold mb-1.5 uppercase tracking-[0.1em] flex items-center gap-1.5 ${
                                                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                                                        }`}>
                                                            <FaBriefcase size={10} className={isDarkMode ? 'text-amber-400' : 'text-amber-600'} />
                                                            Select Role
                                                            <span className={isDarkMode ? 'text-rose-400' : 'text-red-500'}>*</span>
                                                        </label>
                                                        <div className="relative">
                                                            <Select
                                                                options={roles}
                                                                labelField="name"
                                                                valueField="id"
                                                                placeholder="Choose a role..."
                                                                style={{
                                                                    padding: '0.55rem 0.85rem',
                                                                    fontSize: '11px',
                                                                    fontWeight: '600',
                                                                    borderRadius: '0.75rem',
                                                                    width: '100%',
                                                                    borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : '#e5e7eb',
                                                                    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : '#ffffff',
                                                                    color: isDarkMode ? '#fff' : '#111827',
                                                                    minHeight: '42px',
                                                                    boxShadow: isDarkMode ? 'inset 0 1px 0 rgba(255,255,255,0.03)' : 'none',
                                                                }}
                                                                dropdownStyle={{
                                                                    backgroundColor: isDarkMode ? '#0F172A' : '#ffffff',
                                                                    color: isDarkMode ? '#fff' : '#000',
                                                                    border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e5e7eb',
                                                                    borderRadius: '0.75rem',
                                                                    boxShadow: isDarkMode ? '0 10px 40px rgba(0,0,0,0.6)' : '0 10px 25px rgba(0,0,0,0.08)',
                                                                    zIndex: 9999,
                                                                    position: 'absolute',
                                                                }}
                                                                dropdownPosition="auto"
                                                                onChange={handleSelectChange}
                                                            />
                                                        </div>
                                                        {errors.role && (
                                                            <p className={errorClass}>
                                                                <span className="w-1 h-1 rounded-full bg-rose-500 inline-block" />
                                                                {errors.role.message}
                                                            </p>
                                                        )}
                                                    </div>

                                                    {/* Name */}
                                                    <div className="relative" style={{ zIndex: 1 }}>
                                                        <div className="relative">
                                                            <span className={iconWrapClass}>
                                                                <FaUserCog size={10} className={isDarkMode ? 'text-sky-400' : 'text-blue-600'} />
                                                            </span>
                                                            <input
                                                                className={inputClass(errors.name)}
                                                                placeholder=" "
                                                                type="text"
                                                                id="name"
                                                                {...register('name')}
                                                                onChange={handleInputChange}
                                                            />
                                                            <label htmlFor="name" className={floatingLabel(errors.name)}>Full Name</label>
                                                        </div>
                                                        {errors.name && (
                                                            <p className={errorClass}>
                                                                <span className="w-1 h-1 rounded-full bg-rose-500 inline-block" />
                                                                {errors.name.message}
                                                            </p>
                                                        )}
                                                    </div>

                                                    {/* Company Name */}
                                                    <div className="relative" style={{ zIndex: 1 }}>
                                                        <div className="relative">
                                                            <span className={iconWrapClass}>
                                                                <FaBuilding size={10} className={isDarkMode ? 'text-cyan-400' : 'text-cyan-600'} />
                                                            </span>
                                                            <input
                                                                className={inputClass(errors.company_name)}
                                                                id="company_name"
                                                                type="text"
                                                                placeholder=" "
                                                                {...register('company_name')}
                                                                onChange={handleInputChange}
                                                            />
                                                            <label htmlFor="company_name" className={floatingLabel(errors.company_name)}>Company Name</label>
                                                        </div>
                                                        {errors.company_name && (
                                                            <p className={errorClass}>
                                                                <span className="w-1 h-1 rounded-full bg-rose-500 inline-block" />
                                                                {errors.company_name.message}
                                                            </p>
                                                        )}
                                                    </div>

                                                    {/* Email */}
                                                    <div className="relative" style={{ zIndex: 1 }}>
                                                        <div className="relative">
                                                            <span className={iconWrapClass}>
                                                                <FaEnvelope size={10} className={isDarkMode ? 'text-rose-400' : 'text-rose-500'} />
                                                            </span>
                                                            <input
                                                                className={inputClass(errors.email)}
                                                                id="email"
                                                                type="email"
                                                                placeholder=" "
                                                                {...register('email')}
                                                            />
                                                            <label htmlFor="email" className={floatingLabel(errors.email)}>Email Address</label>
                                                        </div>
                                                        {errors.email && (
                                                            <p className={errorClass}>
                                                                <span className="w-1 h-1 rounded-full bg-rose-500 inline-block" />
                                                                {errors.email.message}
                                                            </p>
                                                        )}
                                                    </div>

                                                    {/* Phone */}
                                                    <div className="relative" style={{ zIndex: 1 }}>
                                                        <div className="relative">
                                                            <span className={iconWrapClass}>
                                                                <FaPhone size={10} className={isDarkMode ? 'text-emerald-400' : 'text-emerald-600'} />
                                                            </span>
                                                            <input
                                                                className={inputClass(errors.phone)}
                                                                id="phone"
                                                                type="tel"
                                                                maxLength="10"
                                                                placeholder=" "
                                                                {...register('phone')}
                                                                onChange={handleInputChange}
                                                            />
                                                            <label htmlFor="phone" className={floatingLabel(errors.phone)}>Phone Number</label>
                                                        </div>
                                                        {errors.phone && (
                                                            <p className={errorClass}>
                                                                <span className="w-1 h-1 rounded-full bg-rose-500 inline-block" />
                                                                {errors.phone.message}
                                                            </p>
                                                        )}
                                                    </div>

                                                    {/* Department */}
                                                    <div className="relative" style={{ zIndex: 1 }}>
                                                        <div className="relative">
                                                            <span className={iconWrapClass}>
                                                                <FaBriefcase size={10} className={isDarkMode ? 'text-amber-400' : 'text-amber-600'} />
                                                            </span>
                                                            <input
                                                                className={inputClass(errors.department)}
                                                                id="department"
                                                                type="text"
                                                                placeholder=" "
                                                                {...register('department')}
                                                                onChange={handleInputChange}
                                                            />
                                                            <label htmlFor="department" className={floatingLabel(errors.department)}>Department</label>
                                                        </div>
                                                        {errors.department && (
                                                            <p className={errorClass}>
                                                                <span className="w-1 h-1 rounded-full bg-rose-500 inline-block" />
                                                                {errors.department.message}
                                                            </p>
                                                        )}
                                                    </div>

                                                    {/* Password */}
                                                    <div className="relative" style={{ zIndex: 1 }}>
                                                        <div className="relative">
                                                            <span className={iconWrapClass}>
                                                                <FaLock size={10} className={isDarkMode ? 'text-violet-400' : 'text-violet-600'} />
                                                            </span>
                                                            <input
                                                                className={`${inputClass(errors.password)} pr-10`}
                                                                id="password"
                                                                type={showPassword ? 'text' : 'password'}
                                                                placeholder=" "
                                                                {...register('password')}
                                                            />
                                                            <label htmlFor="password" className={floatingLabel(errors.password)}>Password</label>
                                                            <button
                                                                type="button"
                                                                className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md transition-all duration-200 ${
                                                                    isDarkMode
                                                                        ? 'text-gray-500 hover:text-gray-300 hover:bg-white/[0.05]'
                                                                        : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                                                                }`}
                                                                onClick={() => setShowPassword(!showPassword)}
                                                            >
                                                                {showPassword ? <FiEyeOff size={13} /> : <FiEye size={13} />}
                                                            </button>
                                                        </div>
                                                        {errors.password && (
                                                            <p className={errorClass}>
                                                                <span className="w-1 h-1 rounded-full bg-rose-500 inline-block" />
                                                                {errors.password.message}
                                                            </p>
                                                        )}
                                                    </div>

                                                </div>

                                                {/* Submit */}
                                                <div className={`flex items-center justify-end mt-5 pt-4 border-t gap-2 ${
                                                    isDarkMode ? 'border-white/[0.06]' : 'border-gray-100'
                                                }`}>
                                                    <button
                                                        type="button"
                                                        onClick={() => reset()}
                                                        className={`text-[10px] font-semibold px-4 py-2.5 rounded-xl border transition-all duration-300 ${
                                                            isDarkMode
                                                                ? 'text-gray-400 border-white/10 hover:bg-white/5 hover:text-gray-200'
                                                                : 'text-gray-500 border-gray-200 hover:bg-gray-50 hover:text-gray-700'
                                                        }`}
                                                    >
                                                        Reset
                                                    </button>
                                                    <button
                                                        className={`group relative text-[11px] font-bold py-2.5 px-6 rounded-xl transition-all duration-300 flex items-center gap-2 overflow-hidden ${
                                                            isDarkMode
                                                                ? 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white shadow-[0_0_30px_rgba(139,92,246,0.4)] hover:shadow-[0_0_50px_rgba(139,92,246,0.7)] hover:scale-[1.03]'
                                                                : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:opacity-90 shadow-md hover:shadow-lg'
                                                        }`}
                                                        type="submit"
                                                        disabled={isSubmitting}
                                                    >
                                                        {!isSubmitting && (
                                                            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000" />
                                                        )}
                                                        {isSubmitting ? (
                                                            <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin relative z-10" />
                                                        ) : (
                                                            <FaUserPlus size={11} className="relative z-10 group-hover:scale-110 transition-transform duration-300" />
                                                        )}
                                                        <span className="relative z-10">{isSubmitting ? "Creating..." : 'Create User'}</span>
                                                    </button>
                                                </div>
                                            </form>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Footer Note */}
                            <div className="mt-3 text-center">
                                <p className={`text-[9px] font-medium ${
                                    isDarkMode ? 'text-gray-500' : 'text-gray-400'
                                }`}>
                                    All fields marked with <span className={isDarkMode ? 'text-rose-400' : 'text-red-500'}>*</span> are required
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddUser;